import React, { useState, useEffect } from 'react';
import { X, Shield, Send, Share2, User, Mail, Phone, FileText, Calendar } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface ExpertModalProps {
  onClose: () => void;
  currentEnquete?: {
    id: string;
    titre: string;
    description: string;
    client: any;
    is_shared_with_admin?: boolean;
    shared_by_name?: string;
    shared_at?: string;
  };
  onShareStatusChange?: (isShared: boolean, sharedByName?: string, sharedAt?: string) => void;
}

const services = [
  {
    title: 'Vérification email + téléphone + pseudo',
    price: 50,
    description: 'Analyse et vérification des informations de contact et d\'identité numérique.',
    id: 'verif',
    deliveryTime: '24-48h',
    includes: ['Vérification email', 'Validation téléphone', 'Recherche pseudonyme', 'Rapport synthèse']
  },
  {
    title: 'Profil complet réseaux sociaux',
    price: 100,
    description: 'Recherche approfondie et profilage sur les réseaux sociaux.',
    id: 'social',
    deliveryTime: '2-3 jours',
    includes: ['Facebook, Instagram, Twitter', 'LinkedIn professionnel', 'TikTok, Snapchat', 'Analyse comportementale']
  },
  {
    title: 'Rapport OSINT détaillé / Investigation',
    price: 500,
    description: 'Rapport complet d\'investigation OSINT par un expert certifié.',
    id: 'osint',
    deliveryTime: '5-7 jours',
    includes: ['Investigation complète', 'Rapport détaillé 20+ pages', 'Sources vérifiées', 'Recommandations']
  },
  {
    title: 'Précédentes enquêtes, actifs financiers & patrimoine',
    price: 1000,
    description: 'Recherche sur les antécédents, patrimoine et actifs financiers.',
    id: 'patrimoine',
    deliveryTime: '7-10 jours',
    includes: ['Recherche antécédents', 'Analyse patrimoine', 'Actifs financiers', 'Rapport confidentiel']
  }
];

const ExpertModal: React.FC<ExpertModalProps> = ({ onClose, currentEnquete, onShareStatusChange }) => {
  const { user } = useAuth();
  const [step, setStep] = useState<'services' | 'form' | 'share'>('services');
  const [selectedService, setSelectedService] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isSharedWithAdmin, setIsSharedWithAdmin] = useState(currentEnquete?.is_shared_with_admin || false);
  const [formData, setFormData] = useState({
    targetInfo: '',
    specificRequests: '',
    urgency: 'normal' as 'normal' | 'urgent' | 'express',
    contactEmail: user?.email || '',
    contactPhone: '',
    additionalNotes: '',
    shareEnquete: false
  });

  // Auto-remplir l'email de contact avec l'email de l'utilisateur connecté
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, contactEmail: user.email }));
    }
  }, [user]);

  const handleServiceSelect = (service: any) => {
    setSelectedService(service);
    setStep('form');
  };

  const handleSubmitRequest = async () => {
    try {
      setLoading(true);
      
      const requestData = {
        serviceId: selectedService.id,
        serviceTitle: selectedService.title,
        price: selectedService.price,
        formData,
        enqueteId: currentEnquete?.id,
        shareEnquete: formData.shareEnquete
      };

      // Appel API pour créer la demande d\'expert
      await apiService.post('/expert-requests', requestData);
      
      alert('Demande envoyée avec succès! Vous recevrez une confirmation par email.');
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      alert('Erreur lors de l\'envoi de la demande. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleShareEnquete = async () => {
    if (isSharedWithAdmin) {
      alert('Cette enquête est déjà partagée avec l\'administrateur.');
      return;
    }
    
    try {
      setLoading(true);
      
      await apiService.post('/enquetes/share-with-admin', {
        enqueteId: currentEnquete?.id
      });
      
      setIsSharedWithAdmin(true);
      const now = new Date().toISOString();
      onShareStatusChange?.(true, 'Vous', now);
      alert('Enquête partagée avec l\'administrateur avec succès!');
      // Ne pas fermer le modal, laisser l'utilisateur continuer
    } catch (error) {
      console.error('Erreur lors du partage:', error);
      if (error instanceof Error && error.message.includes('déjà partagée')) {
        setIsSharedWithAdmin(true);
        alert('Cette enquête est déjà partagée avec l\'administrateur.');
      } else {
        alert('Erreur lors du partage. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnshareEnquete = async () => {
    if (!isSharedWithAdmin) {
      return;
    }
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le partage avec l\'administrateur ?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      await apiService.delete(`/enquetes/${currentEnquete?.id}/share-with-admin`);
      
      setIsSharedWithAdmin(false);
      onShareStatusChange?.(false);
      alert('Partage supprimé avec succès!');
    } catch (error) {
      console.error('Erreur lors de la suppression du partage:', error);
      alert('Erreur lors de la suppression du partage. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!selectedService) return 0;
    let price = selectedService.price;
    if (formData.urgency === 'urgent') price *= 1.5;
    if (formData.urgency === 'express') price *= 2;
    return price;
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }} onClick={onClose}>
      <div className="w-full max-w-2xl osint-card rounded-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-osint-light-gray">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-osint-orange rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Faire appel à un expert</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Fermer la modale"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu dynamique selon l'étape */}
        <div className="p-6 overflow-y-auto">
          {step === 'services' && (
            <div className="space-y-4">
              {currentEnquete && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        <h3 className="text-white font-semibold">Enquête en cours</h3>
                      </div>
                      <p className="text-gray-300 text-sm font-medium">{currentEnquete.titre}</p>
                      <p className="text-gray-400 text-xs mt-1">{currentEnquete.description?.substring(0, 100)}...</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 mt-4">
                    {!isSharedWithAdmin ? (
                      <button
                        onClick={handleShareEnquete}
                        disabled={loading}
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600/80 hover:bg-blue-600 border border-blue-500 rounded-lg text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Share2 className="w-4 h-4" />
                        <span>{loading ? 'Partage...' : 'Partager avec admin'}</span>
                      </button>
                    ) : (
                      <div className="flex-1 flex space-x-2">
                        <button
                          disabled={true}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-green-600/20 border border-green-500/50 rounded-lg text-green-400 text-sm font-medium cursor-not-allowed"
                        >
                          <Share2 className="w-4 h-4" />
                          <span>Déjà partagé</span>
                        </button>
                        <button
                          onClick={handleUnshareEnquete}
                          disabled={loading}
                          className="px-3 py-2 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 rounded-lg text-red-400 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Supprimer le partage"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    <div className="px-3 py-2 bg-green-600/20 border border-green-500/50 rounded-lg text-green-400 text-xs flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                      <span>Active</span>
                    </div>
                  </div>
                  
                  {isSharedWithAdmin && currentEnquete?.shared_by_name && (
                    <div className="mt-2 text-xs text-gray-400">
                      <Shield className="w-3 h-3 inline mr-1" />
                      Partagé par {currentEnquete.shared_by_name}
                      {currentEnquete.shared_at && (
                        <span> le {new Date(currentEnquete.shared_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <h3 className="text-lg font-semibold text-white mb-4">Choisissez votre prestation</h3>
              {services.map(service => (
                <div key={service.id} className="border border-osint-light-gray rounded-lg p-4 bg-osint-light-gray/10 hover:border-osint-orange transition-colors">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <div className="text-base font-semibold text-white mb-1">{service.title}</div>
                      <div className="text-gray-400 text-sm mb-2">{service.description}</div>
                      <div className="flex items-center space-x-4 mb-3">
                        <div className="text-osint-orange font-bold text-lg">{service.price}€</div>
                        <div className="flex items-center space-x-1 text-gray-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>{service.deliveryTime}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Inclus: </span>
                        {service.includes.join(', ')}
                      </div>
                    </div>
                    <button
                      onClick={() => handleServiceSelect(service)}
                      className="osint-button mt-4 md:mt-0 md:ml-6 px-4 py-2 rounded-lg text-white text-sm font-medium"
                    >
                      Commander
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 'form' && selectedService && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <button
                  onClick={() => setStep('services')}
                  className="text-gray-400 hover:text-white"
                >
                  ←
                </button>
                <h3 className="text-lg font-semibold text-white">Commande: {selectedService.title}</h3>
              </div>
              
              <div className="bg-osint-orange/10 border border-osint-orange rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium">{selectedService.title}</span>
                  <span className="text-osint-orange font-bold">{calculatePrice()}€</span>
                </div>
                <p className="text-gray-300 text-sm mt-1">{selectedService.description}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-white font-medium mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Informations à investiguer *
                  </label>
                  <textarea
                    value={formData.targetInfo}
                    onChange={(e) => setFormData({...formData, targetInfo: e.target.value})}
                    placeholder="Nom, email, téléphone, adresse, pseudos, etc."
                    className="w-full p-3 bg-osint-light-gray text-white rounded-lg border border-gray-600 focus:border-osint-orange focus:outline-none"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    <FileText className="w-4 h-4 inline mr-2" />
                    Demandes spécifiques
                  </label>
                  <textarea
                    value={formData.specificRequests}
                    onChange={(e) => setFormData({...formData, specificRequests: e.target.value})}
                    placeholder="Précisez ce que vous recherchez spécifiquement..."
                    className="w-full p-3 bg-osint-light-gray text-white rounded-lg border border-gray-600 focus:border-osint-orange focus:outline-none"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-medium mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email de contact *
                    </label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                      className="w-full p-3 bg-osint-light-gray text-white rounded-lg border border-gray-600 focus:border-osint-orange focus:outline-none"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white font-medium mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                      className="w-full p-3 bg-osint-light-gray text-white rounded-lg border border-gray-600 focus:border-osint-orange focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Urgence</label>
                  <select
                    value={formData.urgency}
                    onChange={(e) => setFormData({...formData, urgency: e.target.value as any})}
                    className="w-full p-3 bg-osint-light-gray text-white rounded-lg border border-gray-600 focus:border-osint-orange focus:outline-none"
                  >
                    <option value="normal">Normal ({selectedService.deliveryTime})</option>
                    <option value="urgent">Urgent (+50% - délai réduit de 50%)</option>
                    <option value="express">Express (+100% - délai réduit de 75%)</option>
                  </select>
                </div>

                {currentEnquete && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="shareEnquete"
                      checked={formData.shareEnquete}
                      onChange={(e) => setFormData({...formData, shareEnquete: e.target.checked})}
                      className="w-4 h-4 text-osint-orange bg-osint-light-gray border-gray-600 rounded focus:ring-osint-orange"
                    />
                    <label htmlFor="shareEnquete" className="text-white text-sm">
                      Partager l'enquête en cours avec l'expert
                    </label>
                  </div>
                )}

                <div>
                  <label className="block text-white font-medium mb-2">Notes supplémentaires</label>
                  <textarea
                    value={formData.additionalNotes}
                    onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
                    placeholder="Informations complémentaires, contexte, etc."
                    className="w-full p-3 bg-osint-light-gray text-white rounded-lg border border-gray-600 focus:border-osint-orange focus:outline-none"
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setStep('services')}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleSubmitRequest}
                  disabled={loading || !formData.targetInfo || !formData.contactEmail}
                  className="flex-1 osint-button px-4 py-2 rounded-lg text-white font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  <span>{loading ? 'Envoi...' : `Commander ${calculatePrice()}€`}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertModal;